import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = {
    class: "mb-5 grid grid-cols-1 items-start gap-5 lg:grid-cols-3",
    "data-testid": "project-resume"
};
const _hoisted_2 = {
    class: "min-w-0",
    "data-testid": "project-resume-sidebar"
};
const _hoisted_3 = { class: "flex flex-col gap-3" };
const _hoisted_4 = { class: "flex flex-col gap-1" };
const _hoisted_5 = { class: "text-xs text-(--colors-text-body-subtle)" };
const _hoisted_6 = { class: "flex flex-col gap-3" };
const _hoisted_7 = {
    class: "flex min-w-0 flex-col gap-5",
    "data-testid": "project-resume-center"
};
const _hoisted_8 = {
    key: 2,
    class: "flex flex-col gap-4"
};
const _hoisted_9 = { class: "flex flex-col gap-1.5" };
const _hoisted_10 = { class: "flex items-center justify-between gap-2 text-xs font-medium" };
const _hoisted_11 = {
    class: "text-(--colors-text-black-default) dark:text-(--colors-text-white-default)",
    "data-testid": "project-resume-collected"
};
const _hoisted_12 = ["aria-valuenow", "aria-label"];
const _hoisted_13 = { key: 1 };
const _hoisted_14 = { class: "flex items-center gap-2 text-xs" };
const _hoisted_15 = { class: "text-(--colors-text-body-subtle)" };
const _hoisted_16 = {
    class: "text-(--colors-text-heading) dark:text-(--colors-text-white-default)",
    "data-testid": "project-resume-objective"
};
const _hoisted_17 = {
    key: 1,
    class: "flex flex-col items-center gap-2 py-2",
    "data-testid": "project-resume-milestones-empty"
};
const _hoisted_18 = { class: "text-xs text-(--colors-text-body-subtle)" };
const _hoisted_19 = {
    key: 2,
    class: "flex flex-col gap-2.5"
};
const _hoisted_20 = { class: "min-w-0 flex-1" };
const _hoisted_21 = { class: "truncate text-xs font-medium text-(--colors-text-heading) dark:text-(--colors-text-white-default)" };
const _hoisted_22 = { class: "text-[11px] text-(--colors-text-body-subtle)" };
const _hoisted_23 = { class: "shrink-0 text-xs font-semibold text-(--colors-text-heading) dark:text-(--colors-text-white-default)" };
const _hoisted_24 = {
    key: 0,
    class: "border-t border-(--colors-border-base-soft) pt-1.5"
};
const _hoisted_25 = {
    key: 3,
    class: "flex flex-col items-center gap-1 py-3 text-center",
    "data-testid": "project-resume-donations-empty"
};
const _hoisted_26 = { class: "text-xs text-(--colors-text-body-subtle)" };
const _hoisted_27 = {
    class: "flex min-w-0 flex-col gap-5",
    "data-testid": "project-resume-right"
};
const _hoisted_28 = { class: "grid grid-cols-2 gap-3" };
const _hoisted_29 = {
    key: 2,
    class: "flex flex-col gap-3"
};
const _hoisted_30 = {
    key: 0,
    class: "text-[11px] text-(--colors-text-body-subtle)"
};
const _hoisted_31 = ["data-testid"];
const _hoisted_32 = {
    key: 0,
    class: "border-t border-(--colors-border-base-soft) pt-1.5"
};
const _hoisted_33 = {
    key: 3,
    class: "flex flex-col items-center gap-1 py-3 text-center",
    "data-testid": "project-resume-campaigns-empty"
};
const _hoisted_34 = { class: "text-xs text-(--colors-text-body-subtle)" };
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Accordion, AccordionItem, Avatar, Badge, Button, Timeline, Tooltip, WidgetShell, } from "@eudonet/opal";
import { useProfileStore } from "@/modules/settings/public";
import { RouterLink } from "vue-router";
import { CampaignStatusBadge } from "@/modules/communications/public";
import { useProjectAmounts } from "../../composables/useProjectAmounts";
import { formatDate } from "@/shared/utils/date-formatting";
import { projectStatusTheme } from "../../composables/projectStatusBadge";
import { milestoneStatusTheme } from "../../composables/milestoneStatusBadge";
import { fundingProgress } from "../../../domain/funding";
import ResumeField from "./ResumeField.vue";
import ResumeLoadError from "./ResumeLoadError.vue";
import ResumeSkeleton from "./ResumeSkeleton.vue";
import ResumeStat from "./ResumeStat.vue";
/** Placeholder for an absent value — every blank field of the Résumé renders it. */
const EMPTY_VALUE = "—";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ProjectTabResume',
    props: {
        project: {},
        donations: {},
        campaigns: { default: () => ({ items: [], totalCampaigns: 0 }) },
        donationsError: { type: Boolean, default: false },
        campaignsError: { type: Boolean, default: false },
        donationsLoading: { type: Boolean, default: false },
        campaignsLoading: { type: Boolean, default: false },
        canEdit: { type: Boolean, default: false }
    },
    emits: ["add-milestone", "go-to-tab", "go-to-campaigns", "retry-donations", "retry-campaigns"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t, locale } = useI18n();
        const profileStore = useProfileStore();
        const { formatProjectAmount } = useProjectAmounts();
        function displayDate(value) {
            if (!value)
                return EMPTY_VALUE;
            return formatDate(value, locale.value, profileStore.profile?.dateFormat);
        }
        // ── Financement ───────────────────────────────────────────────────────────────
        const objective = computed(() => props.project.objectiveAmount ?? 0);
        const hasObjective = computed(() => objective.value > 0);
        const collected = computed(() => props.donations.totalAllocated);
        // To the cent, like the Détails block, the Affectations rows and the list column.
        const collectedDisplay = computed(() => formatProjectAmount(collected.value));
        const objectiveDisplay = computed(() => formatProjectAmount(objective.value));
        const progress = computed(() => fundingProgress(collected.value, objective.value));
        const fundingPercent = computed(() => progress.value?.percent ?? 0);
        const cappedPercent = computed(() => Math.min(100, Math.max(0, fundingPercent.value)));
        const isObjectiveExceeded = computed(() => progress.value?.exceeded ?? false);
        // ── Jalons ────────────────────────────────────────────────────────────────────
        const achievedMilestoneCount = computed(() => props.project.milestones.filter((milestone) => milestone.status === "Achieved").length);
        /**
         * The status word is what the design puts in the timeline badge, with the milestone label as the
         * title and the target date below — so `date` (the badge slot) carries the STATUS here, not a
         * date. Icons are off: a calendar glyph beside "Atteint" would contradict the word.
         */
        const milestoneItems = computed(() => props.project.milestones.map((milestone) => ({
            date: t(`projects.milestoneStatus.${milestone.status}`),
            badgeTheme: milestoneStatusTheme[milestone.status] ?? "neutral",
            title: milestone.label,
            description: displayDate(milestone.targetDate),
        })));
        // ── Derniers dons ─────────────────────────────────────────────────────────────
        const remainingDonationCount = computed(() => Math.max(0, props.donations.totalDonations - props.donations.items.length));
        /** A figure is only shown once its read has settled AND succeeded. */
        const donationsPending = computed(() => props.donationsLoading || props.donationsError);
        const campaignsPending = computed(() => props.campaignsLoading || props.campaignsError);
        // ── Campagnes associées ───────────────────────────────────────────────────────
        const remainingCampaignCount = computed(() => Math.max(0, props.campaigns.totalCampaigns - props.campaigns.items.length));
        /**
         * "Du 11/09/2026 au 11/10/2026" when both bounds are set, one-sided when only one is, and the
         * placeholder for an undated draft.
         */
        function campaignPeriodLabel(campaign) {
            const start = campaign.startDate ? displayDate(campaign.startDate) : null;
            const end = campaign.endDate ? displayDate(campaign.endDate) : null;
            if (start && end)
                return t("projects.detail.resume.campaigns.period", { start, end });
            if (start)
                return t("projects.detail.resume.campaigns.from", { start });
            if (end)
                return t("projects.detail.resume.campaigns.until", { end });
            return EMPTY_VALUE;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createVNode(_unref(WidgetShell), { flush: "" }, {
                        default: _withCtx(() => [
                            _createVNode(_unref(Accordion), {
                                variant: "flush",
                                multiple: true,
                                "heading-level": 2
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_unref(AccordionItem), {
                                        title: _unref(t)('projects.detail.resume.sections.information'),
                                        "default-open": true,
                                        "data-testid": "project-resume-section-information"
                                    }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("dl", _hoisted_3, [
                                                _createVNode(ResumeField, {
                                                    label: _unref(t)('projects.create.actionDomainLabel'),
                                                    value: __props.project.actionDomain,
                                                    testid: "project-resume-action-domain"
                                                }, null, 8 /* PROPS */, ["label", "value"]),
                                                _createVNode(ResumeField, {
                                                    label: _unref(t)('projects.create.codeLabel'),
                                                    value: __props.project.code,
                                                    mono: "",
                                                    testid: "project-resume-code"
                                                }, null, 8 /* PROPS */, ["label", "value"]),
                                                _createVNode(ResumeField, {
                                                    label: _unref(t)('projects.create.startDateLabel'),
                                                    value: displayDate(__props.project.startDate),
                                                    testid: "project-resume-start-date"
                                                }, null, 8 /* PROPS */, ["label", "value"]),
                                                _createVNode(ResumeField, {
                                                    label: _unref(t)('projects.detail.resume.fields.endDate'),
                                                    value: displayDate(__props.project.endDate),
                                                    testid: "project-resume-end-date"
                                                }, null, 8 /* PROPS */, ["label", "value"])
                                            ])
                                        ]),
                                        _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["title"]),
                                    _createVNode(_unref(AccordionItem), {
                                        title: _unref(t)('projects.detail.resume.sections.status'),
                                        "default-open": true,
                                        "data-testid": "project-resume-section-status"
                                    }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_4, [
                                                _createElementVNode("dt", _hoisted_5, _toDisplayString(_unref(t)("projects.create.statusLabel")), 1 /* TEXT */),
                                                _createElementVNode("dd", null, [
                                                    _createVNode(_unref(Badge), {
                                                        label: _unref(t)(`projects.status.${__props.project.status}`),
                                                        theme: _unref(projectStatusTheme)[__props.project.status] ?? 'neutral',
                                                        "show-close-icon": false,
                                                        size: "sm",
                                                        "data-testid": "project-resume-status"
                                                    }, null, 8 /* PROPS */, ["label", "theme"])
                                                ])
                                            ])
                                        ]),
                                        _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["title"]),
                                    _createVNode(_unref(AccordionItem), {
                                        title: _unref(t)('projects.detail.resume.sections.references'),
                                        "data-testid": "project-resume-section-references"
                                    }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("dl", _hoisted_6, [
                                                _createVNode(ResumeField, {
                                                    label: _unref(t)('projects.create.chargeAccountLabel'),
                                                    value: __props.project.chargeAccount,
                                                    mono: "",
                                                    testid: "project-resume-charge-account"
                                                }, null, 8 /* PROPS */, ["label", "value"]),
                                                _createVNode(ResumeField, {
                                                    label: _unref(t)('projects.create.analyticalCodeLabel'),
                                                    value: __props.project.analyticalCode,
                                                    mono: "",
                                                    testid: "project-resume-analytical-code"
                                                }, null, 8 /* PROPS */, ["label", "value"])
                                            ])
                                        ]),
                                        _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["title"])
                                ]),
                                _: 1 /* STABLE */
                            })
                        ]),
                        _: 1 /* STABLE */
                    })
                ]),
                _createElementVNode("div", _hoisted_7, [
                    _createVNode(_unref(WidgetShell), {
                        header: _unref(t)('projects.detail.resume.financing.title'),
                        loading: __props.donationsLoading
                    }, {
                        default: _withCtx(() => [
                            (__props.donationsLoading)
                                ? (_openBlock(), _createBlock(ResumeSkeleton, {
                                    key: 0,
                                    testid: "project-resume-financing-loading"
                                }))
                                : (__props.donationsError)
                                    ? (_openBlock(), _createBlock(ResumeLoadError, {
                                        key: 1,
                                        testid: "project-resume-financing-error",
                                        onRetry: _cache[0] || (_cache[0] = ($event) => (emit('retry-donations')))
                                    }))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                        _createElementVNode("div", _hoisted_9, [
                                            _createElementVNode("div", _hoisted_10, [
                                                _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(t)("projects.detail.resume.financing.collected", { amount: collectedDisplay.value })), 1 /* TEXT */),
                                                (hasObjective.value)
                                                    ? (_openBlock(), _createElementBlock("span", {
                                                        key: 0,
                                                        class: _normalizeClass(isObjectiveExceeded.value
                                                            ? 'text-(--colors-text-success-strong)'
                                                            : 'text-(--colors-text-black-default) dark:text-(--colors-text-white-default)'),
                                                        "data-testid": "project-resume-progress-label"
                                                    }, _toDisplayString(_unref(t)("projects.detail.resume.financing.progress", {
                                                        percent: fundingPercent.value,
                                                        objective: objectiveDisplay.value,
                                                    })), 3 /* TEXT, CLASS */))
                                                    : _createCommentVNode("v-if", true)
                                            ]),
                                            (hasObjective.value)
                                                ? (_openBlock(), _createElementBlock("div", {
                                                    key: 0,
                                                    class: "h-2.5 w-full overflow-hidden rounded-xl bg-(--colors-background-gray)",
                                                    role: "progressbar",
                                                    "aria-valuenow": cappedPercent.value,
                                                    "aria-valuemin": "0",
                                                    "aria-valuemax": "100",
                                                    "aria-label": _unref(t)('projects.detail.resume.financing.title'),
                                                    "data-testid": "project-resume-progress"
                                                }, [
                                                    _createElementVNode("div", {
                                                        class: "h-full rounded-xl bg-(image:--gradient-brand-horizontal)",
                                                        style: _normalizeStyle({ width: `${cappedPercent.value}%` })
                                                    }, null, 4 /* STYLE */)
                                                ], 8 /* PROPS */, _hoisted_12))
                                                : _createCommentVNode("v-if", true),
                                            (isObjectiveExceeded.value)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                                    _createVNode(_unref(Badge), {
                                                        label: _unref(t)('projects.detail.resume.financing.exceeded', { percent: fundingPercent.value }),
                                                        theme: "green",
                                                        "show-close-icon": false,
                                                        size: "sm",
                                                        "data-testid": "project-resume-objective-exceeded"
                                                    }, null, 8 /* PROPS */, ["label"])
                                                ]))
                                                : _createCommentVNode("v-if", true)
                                        ]),
                                        _createElementVNode("div", _hoisted_14, [
                                            _createElementVNode("span", _hoisted_15, _toDisplayString(_unref(t)("projects.detail.resume.financing.objective")), 1 /* TEXT */),
                                            _createElementVNode("span", _hoisted_16, _toDisplayString(hasObjective.value ? objectiveDisplay.value : _unref(t)("projects.detail.resume.financing.noObjective")), 1 /* TEXT */)
                                        ])
                                    ]))
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["header", "loading"]),
                    _createVNode(_unref(WidgetShell), {
                        header: _unref(t)('projects.detail.milestones.title')
                    }, {
                        actions: _withCtx(() => [
                            _createVNode(_unref(Tooltip), {
                                text: _unref(t)('common.permissions.noEditRights'),
                                disabled: __props.canEdit,
                                tabindex: __props.canEdit ? undefined : 0
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_unref(Button), {
                                        variant: "secondary",
                                        size: "sm",
                                        "icon-left": "ri-add-line",
                                        label: _unref(t)('projects.detail.resume.milestones.add'),
                                        disabled: !__props.canEdit,
                                        "data-testid": "project-resume-add-milestone",
                                        onClick: _cache[1] || (_cache[1] = ($event) => (emit('add-milestone')))
                                    }, null, 8 /* PROPS */, ["label", "disabled"])
                                ]),
                                _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["text", "disabled", "tabindex"])
                        ]),
                        default: _withCtx(() => [
                            (milestoneItems.value.length)
                                ? (_openBlock(), _createBlock(_unref(Timeline), {
                                    key: 0,
                                    items: milestoneItems.value,
                                    orientation: "vertical",
                                    "show-item-icons": false,
                                    "aria-label": _unref(t)('projects.detail.milestones.title'),
                                    "data-testid": "project-resume-milestones"
                                }, null, 8 /* PROPS */, ["items", "aria-label"]))
                                : (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                    _createElementVNode("p", _hoisted_18, _toDisplayString(_unref(t)("projects.detail.milestones.empty")), 1 /* TEXT */),
                                    (__props.canEdit)
                                        ? (_openBlock(), _createElementBlock("button", {
                                            key: 0,
                                            type: "button",
                                            class: "text-xs font-medium text-(--colors-text-link) hover:underline",
                                            "data-testid": "project-resume-add-milestone-empty",
                                            onClick: _cache[2] || (_cache[2] = ($event) => (emit('add-milestone')))
                                        }, _toDisplayString(_unref(t)("projects.detail.resume.milestones.addFromDetails")), 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true)
                                ]))
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["header"]),
                    _createVNode(_unref(WidgetShell), {
                        header: _unref(t)('projects.detail.resume.donations.title'),
                        loading: __props.donationsLoading
                    }, {
                        actions: _withCtx(() => [
                            _createElementVNode("button", {
                                type: "button",
                                class: "text-xs font-medium text-(--colors-text-link) hover:underline",
                                "data-testid": "project-resume-donations-view-all",
                                onClick: _cache[3] || (_cache[3] = ($event) => (emit('go-to-tab', 'affectations')))
                            }, _toDisplayString(_unref(t)("projects.detail.resume.viewAll")), 1 /* TEXT */)
                        ]),
                        default: _withCtx(() => [
                            (__props.donationsLoading)
                                ? (_openBlock(), _createBlock(ResumeSkeleton, {
                                    key: 0,
                                    testid: "project-resume-donations-loading",
                                    rows: 3
                                }))
                                : (__props.donationsError)
                                    ? (_openBlock(), _createBlock(ResumeLoadError, {
                                        key: 1,
                                        testid: "project-resume-donations-error",
                                        onRetry: _cache[4] || (_cache[4] = ($event) => (emit('retry-donations')))
                                    }))
                                    : (__props.donations.items.length)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.donations.items, (donation) => {
                                                return (_openBlock(), _createElementBlock("div", {
                                                    key: donation.donationId,
                                                    class: "flex items-center gap-2",
                                                    "data-testid": "project-resume-donation-row"
                                                }, [
                                                    _createVNode(_unref(Avatar), {
                                                        name: donation.donorDisplayName ?? '',
                                                        size: "sm",
                                                        "avatar-style": "square",
                                                        presentational: ""
                                                    }, null, 8 /* PROPS */, ["name"]),
                                                    _createElementVNode("div", _hoisted_20, [
                                                        _createElementVNode("p", _hoisted_21, _toDisplayString(donation.donorDisplayName ?? EMPTY_VALUE), 1 /* TEXT */),
                                                        _createElementVNode("p", _hoisted_22, _toDisplayString(displayDate(donation.donationDate)), 1 /* TEXT */)
                                                    ]),
                                                    _createElementVNode("span", _hoisted_23, _toDisplayString(_unref(formatProjectAmount)(donation.allocatedAmount)), 1 /* TEXT */)
                                                ]));
                                            }), 128 /* KEYED_FRAGMENT */)),
                                            (remainingDonationCount.value > 0)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                                                    _createElementVNode("button", {
                                                        type: "button",
                                                        class: "text-xs font-medium text-(--colors-text-link) hover:underline",
                                                        "data-testid": "project-resume-donations-more",
                                                        onClick: _cache[5] || (_cache[5] = ($event) => (emit('go-to-tab', 'affectations')))
                                                    }, _toDisplayString(_unref(t)("projects.detail.resume.donations.more", { count: remainingDonationCount.value })), 1 /* TEXT */)
                                                ]))
                                                : _createCommentVNode("v-if", true)
                                        ]))
                                        : (_openBlock(), _createElementBlock("div", _hoisted_25, [
                                            _cache[9] || (_cache[9] = _createElementVNode("span", {
                                                class: "ri-hand-coin-line text-xl text-(--icons-icon-disabled-light)",
                                                "aria-hidden": "true"
                                            }, null, -1 /* CACHED */)),
                                            _createElementVNode("p", _hoisted_26, _toDisplayString(_unref(t)("projects.detail.resume.donations.empty")), 1 /* TEXT */)
                                        ]))
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["header", "loading"])
                ]),
                _createElementVNode("div", _hoisted_27, [
                    _createVNode(_unref(WidgetShell), {
                        header: _unref(t)('projects.detail.resume.kpis.title')
                    }, {
                        default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_28, [
                                _createVNode(ResumeStat, {
                                    value: `${achievedMilestoneCount.value} / ${__props.project.milestones.length}`,
                                    label: _unref(t)('projects.detail.resume.kpis.milestonesAchieved'),
                                    testid: "project-resume-kpi-milestones"
                                }, null, 8 /* PROPS */, ["value", "label"]),
                                _createVNode(ResumeStat, {
                                    value: donationsPending.value ? EMPTY_VALUE : String(__props.donations.totalDonations),
                                    label: _unref(t)('projects.detail.resume.kpis.donationsAllocated'),
                                    testid: "project-resume-kpi-donations"
                                }, null, 8 /* PROPS */, ["value", "label"]),
                                _createVNode(ResumeStat, {
                                    value: campaignsPending.value ? EMPTY_VALUE : String(__props.campaigns.totalCampaigns),
                                    label: _unref(t)('projects.detail.resume.kpis.campaignCount'),
                                    testid: "project-resume-kpi-campaigns"
                                }, null, 8 /* PROPS */, ["value", "label"]),
                                _createVNode(ResumeStat, {
                                    value: donationsPending.value ? EMPTY_VALUE : collectedDisplay.value,
                                    label: _unref(t)('projects.detail.resume.kpis.collectedAmount'),
                                    testid: "project-resume-kpi-collected"
                                }, null, 8 /* PROPS */, ["value", "label"])
                            ])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["header"]),
                    _createVNode(_unref(WidgetShell), {
                        header: _unref(t)('projects.detail.resume.campaigns.title'),
                        loading: __props.campaignsLoading
                    }, {
                        actions: _withCtx(() => [
                            _createElementVNode("button", {
                                type: "button",
                                class: "text-xs font-medium text-(--colors-text-link) hover:underline",
                                "data-testid": "project-resume-campaigns-view-all",
                                onClick: _cache[6] || (_cache[6] = ($event) => (emit('go-to-campaigns')))
                            }, _toDisplayString(_unref(t)("projects.detail.resume.viewAll")), 1 /* TEXT */)
                        ]),
                        default: _withCtx(() => [
                            (__props.campaignsLoading)
                                ? (_openBlock(), _createBlock(ResumeSkeleton, {
                                    key: 0,
                                    testid: "project-resume-campaigns-loading",
                                    rows: 3
                                }))
                                : (__props.campaignsError)
                                    ? (_openBlock(), _createBlock(ResumeLoadError, {
                                        key: 1,
                                        testid: "project-resume-campaigns-error",
                                        onRetry: _cache[7] || (_cache[7] = ($event) => (emit('retry-campaigns')))
                                    }))
                                    : (__props.campaigns.items.length)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.campaigns.items, (campaign) => {
                                                return (_openBlock(), _createElementBlock("div", {
                                                    key: campaign.id,
                                                    class: "flex flex-col gap-1 rounded-xl border border-(--colors-border-base-medium) p-3",
                                                    "data-testid": "project-resume-campaign-row"
                                                }, [
                                                    _createVNode(_unref(RouterLink), {
                                                        to: { name: 'CampaignDetail', params: { id: campaign.id } },
                                                        class: "truncate text-xs font-semibold text-(--colors-text-link) hover:underline",
                                                        "data-testid": `project-resume-campaign-link-${campaign.id}`
                                                    }, {
                                                        default: _withCtx(() => [
                                                            _createTextVNode(_toDisplayString(campaign.name), 1 /* TEXT */)
                                                        ]),
                                                        _: 2 /* DYNAMIC */
                                                    }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["to", "data-testid"]),
                                                    (campaign.code)
                                                        ? (_openBlock(), _createElementBlock("p", _hoisted_30, _toDisplayString(campaign.code), 1 /* TEXT */))
                                                        : _createCommentVNode("v-if", true),
                                                    _createElementVNode("p", {
                                                        class: "text-[11px] text-(--colors-text-body-subtle)",
                                                        "data-testid": `project-resume-campaign-period-${campaign.id}`
                                                    }, _toDisplayString(campaignPeriodLabel(campaign)), 9 /* TEXT, PROPS */, _hoisted_31),
                                                    _createVNode(_unref(CampaignStatusBadge), {
                                                        class: "self-start",
                                                        status: campaign.status,
                                                        "data-testid": `project-resume-campaign-status-${campaign.id}`
                                                    }, null, 8 /* PROPS */, ["status", "data-testid"])
                                                ]));
                                            }), 128 /* KEYED_FRAGMENT */)),
                                            (remainingCampaignCount.value > 0)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
                                                    _createElementVNode("button", {
                                                        type: "button",
                                                        class: "text-xs font-medium text-(--colors-text-link) hover:underline",
                                                        "data-testid": "project-resume-campaigns-more",
                                                        onClick: _cache[8] || (_cache[8] = ($event) => (emit('go-to-campaigns')))
                                                    }, _toDisplayString(_unref(t)("projects.detail.resume.campaigns.more", { count: remainingCampaignCount.value })), 1 /* TEXT */)
                                                ]))
                                                : _createCommentVNode("v-if", true)
                                        ]))
                                        : (_openBlock(), _createElementBlock("div", _hoisted_33, [
                                            _cache[10] || (_cache[10] = _createElementVNode("span", {
                                                class: "ri-megaphone-line text-xl text-(--icons-icon-disabled-light)",
                                                "aria-hidden": "true"
                                            }, null, -1 /* CACHED */)),
                                            _createElementVNode("p", _hoisted_34, _toDisplayString(_unref(t)("projects.detail.resume.campaigns.empty")), 1 /* TEXT */)
                                        ]))
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["header", "loading"])
                ])
            ]));
        };
    }
});
